import { useState } from "react";
import { X } from "lucide-react";
import { STAGES, SOURCES, ALL_TAGS } from "@/lib/constants";

const empty = {
  name: "",
  email: "",
  phone: "",
  company: "",
  stage: "new_lead",
  monthly: "",
  setup_fee: "",
  source: "Manual",
  tags: [],
  notes: "",
};

export default function AddProspectModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    const payload = {
      ...form,
      monthly: Number(form.monthly) || 0,
      setup_fee: Number(form.setup_fee) || 0,
      call_scheduled: ["call_scheduled", "follow_up_close", "closed_won"].includes(form.stage),
      call_completed: ["follow_up_close", "closed_won"].includes(form.stage),
    };
    await onSubmit(payload);
    setSubmitting(false);
    setForm(empty);
  };

  const inputCls =
    "w-full bg-[#0D1322] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/60 transition-colors";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-prospect-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg gos-card p-6 max-h-[90vh] overflow-y-auto" style={{ background: "#0F2A47" }}>
        <button
          data-testid="close-modal-btn"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>
        <h2 className="font-display text-2xl font-bold text-white">Add Prospect</h2>
        <p className="text-sm text-slate-400 mb-5">Manually register a new lead in the pipeline.</p>

        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="prospect-name-input"
            autoFocus
            placeholder="Full name *"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
          />
          <input
            data-testid="prospect-email-input"
            placeholder="Email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className={inputCls}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              data-testid="prospect-phone-input"
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className={inputCls}
            />
            <input
              data-testid="prospect-company-input"
              placeholder="Company / website"
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Stage</label>
              <select
                data-testid="prospect-stage-select"
                value={form.stage}
                onChange={(e) => set("stage", e.target.value)}
                className={inputCls}
              >
                {STAGES.map((s) => (
                  <option key={s.id} value={s.id} className="bg-[#11182B]">
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Lead source</label>
              <select
                data-testid="prospect-source-select"
                value={form.source}
                onChange={(e) => set("source", e.target.value)}
                className={inputCls}
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s} className="bg-[#11182B]">
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Setup fee — one-time ($)</label>
              <input
                data-testid="prospect-setup-input"
                type="number"
                placeholder="0"
                value={form.setup_fee}
                onChange={(e) => set("setup_fee", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1.5 block">Monthly ($ / mo)</label>
              <input
                data-testid="prospect-monthly-input"
                type="number"
                placeholder="0"
                value={form.monthly}
                onChange={(e) => set("monthly", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-slate-400 mb-1.5 block">Tags</label>
            <div className="flex flex-wrap gap-2">
              {ALL_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                    form.tags.includes(t)
                      ? "gos-gradient text-white border-transparent"
                      : "border-white/10 text-slate-400 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <textarea
            data-testid="prospect-notes-input"
            placeholder="Notes / pain points (e.g., losing traffic to Perplexity)"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            className={inputCls + " resize-none"}
          />

          <button
            data-testid="submit-prospect-btn"
            type="submit"
            disabled={submitting || !form.name.trim()}
            className="w-full gos-gradient text-white font-semibold py-3 rounded-xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
          >
            {submitting ? "Adding…" : "Add to Pipeline"}
          </button>
        </form>
      </div>
    </div>
  );
}
